import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectActiveMatterId } from '@/matter/store/selectors'
import {
  selectMatterDraftArtifactVersionIds,
  selectDraftsLoadingForMatter,
  selectDraftsFetchFailedForMatter,
  selectDraftsInitialLoadCompleteForMatter,
  selectDraftsStaleForMatter,
} from '@/drafting/store/selectors'
import { DraftingActions } from '@/drafting/store/slice'

export interface UseMatterDraftsResult {
  matterId: string | null
  draftArtifactVersionIds: string[]
  isError: boolean
  /** True while a matter's drafts can't be shown yet: loading or not-yet-loaded, with nothing cached to fall back on. */
  isLoadingWithoutCachedDrafts: boolean
  /** Force a refetch of the active matter's drafts (e.g. retry after an error). */
  refetch: () => void
}

/**
 * Provides a way to access the active matter's drafts.
 */
export function useMatterDrafts(): UseMatterDraftsResult {
  const dispatch = useAppDispatch()

  const matterId = useAppSelector((state: RootState) => selectActiveMatterId(state))
  const draftArtifactVersionIds = useAppSelector((state: RootState) => selectMatterDraftArtifactVersionIds(state, { matterId }))
  const isLoading = useAppSelector((state: RootState) => selectDraftsLoadingForMatter(state, { matterId }))
  const isError = useAppSelector((state: RootState) => selectDraftsFetchFailedForMatter(state, { matterId }))
  const initialLoadComplete = useAppSelector((state: RootState) => selectDraftsInitialLoadCompleteForMatter(state, { matterId }))
  const draftsStale = useAppSelector((state: RootState) => selectDraftsStaleForMatter(state, { matterId }))

  // Fetch on mount/matter change, and again when the drafts go stale while mounted (draftsStale), so the
  // view updates live. The listener no-ops if the cache is still fresh.
  useEffect(() => {
    if (!matterId) return
    dispatch(DraftingActions.refreshMatterDrafts({ matterId, forceRefresh: false }))
  }, [matterId, draftsStale, dispatch])

  const refetch = useCallback(() => {
    if (!matterId) return
    dispatch(DraftingActions.refreshMatterDrafts({ matterId, forceRefresh: true }))
  }, [matterId, dispatch])

  // No drafts to show yet and a load is happening or still pending. False with no matter (nothing to load);
  // a background refresh with cached drafts also stays false.
  const isLoadingWithoutCachedDrafts = matterId !== null && draftArtifactVersionIds.length === 0 && (isLoading || !initialLoadComplete)

  return { matterId, draftArtifactVersionIds, isError, isLoadingWithoutCachedDrafts, refetch }
}
